import React, { useState, useCallback } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import Button from 'components/Buttons/Button';
import Autocomplete from 'components/MaterialAutocompleteInput';
import MaterialInput from 'components/MaterialInput';
import ProgressSteps from 'components/ProgressSteps';
import UserOnboardingCard from 'components/UserOnboardingCard';
import { WizardProps } from 'components/Wizard';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { formatTrim } from 'utils/generic';

import {
  Container,
  TransitionContainer,
  FragmentContainer,
  StyledForm,
  PaddingContainer,
  Header,
  UsersContainer,
  GroupButton,
  Subtitle,
  InfoContainer,
  ApproverInfoContainer,
  SpecificInfoContainer,
} from '../../styles';

interface Props {
  wizard: WizardProps;
  adminEmail?: string;
  adminName?: string;
  adminLastName?: string;
}

const ApproversOnboarding = ({ wizard, adminEmail, adminName, adminLastName }: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const [isEdit, setIsEdit] = useState(false);
  const [userEdit, setUserEdit] = useState({ email: '' });
  const [isFocus, setIsFocus] = useState(false);
  const initialValues = {
    email: '',
    name: '',
    lastName: '',
  };

  const validationSchema = yup.object().shape({
    email: yup.string().email().noPlusSign().required().max(100),
    name: yup.string().trim().required().max(100),
    lastName: yup.string().trim().required().max(100),
  });

  const setFocus = useCallback(() => {
    setIsFocus(true);
  }, []);

  const onSelectAutoComplete = useCallback(
    async (e, setFieldValue, setFieldTouched) => {
      if (wizard.data.users.some((user) => formatTrim(user.email) === formatTrim(e.target.value))) {
        const index = wizard.data.users.findIndex((user) => formatTrim(user.email) === formatTrim(e.target.value));
        const user = wizard.data.users[index];
        await setFieldValue('email', formatTrim(user.email));
        await setFieldValue('name', user.name);
        await setFieldValue('lastName', user.lastName);
        await setFieldTouched('email', false);
      } else {
        await setFieldValue('email', e.target.value);
        await setFieldTouched('email', true);
      }
    },
    [wizard],
  );

  const handleSubmit = useCallback(
    async (data, { setFieldValue, setFieldTouched }) => {
      const { approvers } = wizard.data;

      if (approvers.some((approver) => formatTrim(approver.email) === formatTrim(data.email))) {
        toast.add({ type: 'danger', message: t('approversOnboardingForm.emailAlreadyAdd') });
        return;
      }

      setIsFocus(false);
      approvers.push({
        email: formatTrim(data.email),
        name: data.name,
        lastName: data.lastName,
      });
      wizard.setData({
        ...wizard.data,
        approvers,
      });

      await setFieldValue('email', '');
      await setFieldValue('name', '');
      await setFieldValue('lastName', '');
      await setFieldTouched('email', false);
      await setFieldTouched('name', false);
      await setFieldTouched('lastName', false);
    },
    [wizard, t, toast],
  );

  const toogleEdit = useCallback(
    (email: string) => {
      setIsEdit(true);
      setUserEdit(wizard.data.approvers.find((approver) => formatTrim(approver.email) === formatTrim(email)));
    },
    [wizard],
  );

  const handleEdit = useCallback(
    (data) => {
      const { approvers } = wizard.data;

      if (
        approvers
          .filter((approver) => formatTrim(approver.email) !== formatTrim(userEdit.email))
          .some((approver) => formatTrim(approver.email) === formatTrim(data.email))
      ) {
        toast.add({ type: 'danger', message: t('approversOnboardingForm.emailAlreadyAdd') });
        return;
      }

      const index = approvers.findIndex((approver) => formatTrim(approver.email) === formatTrim(userEdit.email));
      approvers[index] = data;
      wizard.setData({
        ...wizard.data,
        approvers,
      });
      setIsEdit(false);
      setUserEdit({ email: '' });
    },
    [wizard, userEdit, t, toast],
  );

  const isSimpleSelected = wizard.data.approvalType === 'SIMPLE';
  const numberApprovers = isSimpleSelected ? 1 : 2;

  const handleUsersAutoComplete = (users: any) => {
    if (adminEmail && adminName && adminLastName) {
      if (!users.find((user) => user.email === adminEmail)) {
        users.push({ email: adminEmail, name: adminName, lastName: adminLastName });
      }
    }

    return users.map((user) => formatTrim(user.email));
  };

  const renderForm = () => {
    if (wizard.data.approvers.length < numberApprovers) {
      return (
        <FragmentContainer key="initial-state">
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initialValues}
            validationSchema={validationSchema}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isValid, setFieldValue, setFieldTouched }: FormikValues) => (
              <>
                <StyledForm>
                  <PaddingContainer>
                    <Header>
                      <h3>
                        {t('approversOnboardingForm.headerInitialState', {
                          number: numberApprovers - wizard.data.approvers.length,
                        })}
                      </h3>
                    </Header>

                    <UsersContainer>
                      {wizard.data.approvers.map((u, index: number) => (
                        <UserOnboardingCard
                          key={formatTrim(u.email)}
                          email={u.email}
                          lastName={u.lastName}
                          name={u.name}
                          isCurrent={index + 1 === wizard.data.approvers.length}
                          isOpen={!isFocus && index + 1 === wizard.data.approvers.length}
                        />
                      ))}
                    </UsersContainer>

                    <Autocomplete
                      name="email"
                      onFocus={setFocus}
                      onSelect={(event) => {
                        onSelectAutoComplete(event, setFieldValue, setFieldTouched);
                      }}
                      label={t('approversOnboardingForm.email')}
                      options={handleUsersAutoComplete(wizard.data.users)}
                    />

                    <MaterialInput name="name" onFocus={setFocus} label={t('approversOnboardingForm.name')} />

                    <MaterialInput name="lastName" onFocus={setFocus} label={t('approversOnboardingForm.lastName')} />
                    {!isFocus && (
                      <InfoContainer type="leftTop">
                        <p>{t('approversOnboardingForm.infoInitialState')}</p>
                      </InfoContainer>
                    )}
                  </PaddingContainer>
                  <GroupButton>
                    <Button disabled={!isValid} value={t('approversOnboardingForm.aggregateButton')} />
                  </GroupButton>
                </StyledForm>
              </>
            )}
          </Formik>
        </FragmentContainer>
      );
    }
    return <></>;
  };

  const renderShowUsers = () => {
    if (isEdit) {
      return (
        <FragmentContainer key="edit-state">
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={userEdit}
            validationSchema={validationSchema}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            onSubmit={handleEdit}
          >
            {({ isValid }: FormikValues) => (
              <>
                <StyledForm>
                  <PaddingContainer>
                    <Header>
                      <h3>{t('approversOnboardingForm.edit')}</h3>
                    </Header>

                    <MaterialInput
                      name="email"
                      onFocus={setFocus}
                      label={t('approversOnboardingForm.email')}
                      type="email"
                    />

                    <MaterialInput name="name" onFocus={setFocus} label={t('approversOnboardingForm.name')} />

                    <MaterialInput name="lastName" onFocus={setFocus} label={t('approversOnboardingForm.lastName')} />
                  </PaddingContainer>
                  <GroupButton>
                    <Button disabled={!isValid} value={t('approversOnboardingForm.saveButton')} />
                  </GroupButton>
                </StyledForm>
              </>
            )}
          </Formik>
        </FragmentContainer>
      );
    }
    if (wizard.data.approvers.length >= numberApprovers) {
      return (
        <FragmentContainer key="show-users">
          <PaddingContainer>
            <Header>
              <h3>{t('approversOnboardingForm.headerShowusers')}</h3>
              <Subtitle>{t('approversOnboardingForm.subtitleShowusers')}</Subtitle>
            </Header>

            <UsersContainer>
              {wizard.data.approvers.map((u) => (
                <UserOnboardingCard
                  key={formatTrim(u.email)}
                  email={u.email}
                  lastName={u.lastName}
                  name={u.name}
                  isCurrent={false}
                  isOpen
                  isEdit
                  onClickOpen={toogleEdit}
                />
              ))}
            </UsersContainer>
            <ApproverInfoContainer>
              <img alt="approver" src="/corporate/images/approvers-onboarding.svg" />
              <SpecificInfoContainer>
                <InfoContainer type="leftBottom">
                  <p>{t('approversOnboardingForm.infoShowusersState')}</p>
                </InfoContainer>
              </SpecificInfoContainer>
            </ApproverInfoContainer>
          </PaddingContainer>
          <GroupButton>
            <Button
              onClick={() => {
                wizard.nextStep();
              }}
              disabled={false}
              value={t('approversOnboardingForm.nextButton')}
            />
          </GroupButton>
        </FragmentContainer>
      );
    }

    return <></>;
  };

  return (
    <Container>
      <nav>
        <AppBar goBack={() => wizard.previousStep()}>{t('approversOnboardingForm.title')}</AppBar>
        <ProgressSteps size={wizard.size} currentStep={wizard.currentStep} />
      </nav>
      <TransitionContainer>
        <ReactCSSTransitionGroup transitionName="fade">
          {renderForm()}
          {renderShowUsers()}
        </ReactCSSTransitionGroup>
      </TransitionContainer>
    </Container>
  );
};

ApproversOnboarding.defaultProps = {
  wizard: {},
};

export default ApproversOnboarding;
